import { useRequest } from 'ahooks';
import { useAuth } from 'auth/AuthContext';
import useIsAppVisible from 'hooks/useIsAppVisible';
import { useCallback, useEffect, useRef, useState } from 'react';
import ApiUsersManager from '../services/api/ApiUsersManager';

interface ActivityTrackerProps {
  userId: string;
}

export default function ActivityTracker(props: ActivityTrackerProps) {
  void props.userId;
  const { registerPreLogoutCallback } = useAuth();
  const [sessionStart] = useState(() => new Date().toISOString());
  const [activeSeconds, setActiveSeconds] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const isAppVisible = useIsAppVisible();

  const activityTimeoutRef = useRef<number | null>(null);
  const updateIntervalRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const activeSecondsRef = useRef<number>(0);

  // Reset activity timer
  const resetActivityTimer = useCallback(() => {
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    activityTimeoutRef.current = window.setTimeout(() => {
      setIsActive(false);
    }, 10000); // 10 seconds of inactivity
  }, []);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    if (!isActive) {
      setIsActive(true);
    }

    resetActivityTimer();
  }, [isActive, resetActivityTimer]);

  // handle app visibility
  useEffect(() => {
    if (isAppVisible) {
      handleUserActivity();
    } else {
      setIsActive(false);
      sendTimeUpdate(sessionStart, activeSecondsRef.current);
    }
  }, [isAppVisible, resetActivityTimer]);

  // Send time update to backend
  const { run: sendTimeUpdate } = useRequest(
    (session_start: string, seconds_spent: number) =>
      ApiUsersManager.updateTimeTracking({ session_start, seconds_spent }),
    {
      manual: true,
    },
  );

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [handleUserActivity]);

  // Timer for counting active seconds
  useEffect(() => {
    if (isActive) {
      const timer = setInterval(() => {
        setActiveSeconds(prev => {
          const newValue = prev + 1;
          activeSecondsRef.current = newValue;
          return newValue;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isActive]);

  // Send updates every 10 seconds when active
  useEffect(() => {
    if (isActive && activeSeconds > 0 && activeSeconds % 10 === 0) {
      sendTimeUpdate(sessionStart, activeSeconds);
    }

    return () => {
      if (updateIntervalRef.current) {
        clearTimeout(updateIntervalRef.current);
      }
    };
  }, [isActive, activeSeconds, sendTimeUpdate]);

  // Send a final time update before tokens are cleared on logout
  useEffect(() => {
    return registerPreLogoutCallback(() => {
      if (activeSecondsRef.current > 0) {
        return ApiUsersManager.updateTimeTracking({
          session_start: sessionStart,
          seconds_spent: activeSecondsRef.current,
        });
      }
    });
  }, [registerPreLogoutCallback, sessionStart]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      if (updateIntervalRef.current) {
        clearTimeout(updateIntervalRef.current);
      }
    };
  }, []);

  // This component doesn't render anything visible
  return null;
}
